import type { SearchResponse } from '@algolia/client-search';
import type { HttpErrorResponse } from '@angular/common/http';
import type { Params } from '@angular/router';
import type { PaginationOption } from '@commons/bo-ui';
import type { Active, Direction } from '@features/partners/builder/settings/versions/versions.service';
import type { LocaleEnum } from '@loyalty/utils/models';
import type { PartnerOverview } from '@models/partner';
import type { PartnerRevision } from '@models/partner-revision';
import type { PartnerRevisionDependencyEnum } from '@models/partner-revision/partner-revision-dependency.enum';
import type { PartnerHistoryInterface } from '@models/partner/partner-history';
import type { SearchParamsObject } from 'algoliasearch';
import type { Observable } from 'rxjs';
import type { PartialDeep } from 'type-fest';

import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { AlgoliaClientProvider } from '@loyalty/utils-angular/algolia';
import { combineLatest, from, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

export type Violation = {
  propertyPath: string;
  title: string;
  type: string;
  value: string;
};

export type PlatformSearchResponse = { productName: string; name: string };

export type CreatePartnerRequest = {
  name: string;
  productName: string;
  locale: LocaleEnum;
};

export type ReplicatePartnerRequest = {
  id: string;
  name: string;
  productName: string;
  locale: LocaleEnum;
};

@Injectable({ providedIn: 'root' })
export class PartnerWebservice {
  readonly #http = inject(HttpClient);
  readonly #algoliaClientProvider = inject(AlgoliaClientProvider);

  #getClient() {
    return this.#algoliaClientProvider.getClient(environment.algolia.clientName)!;
  }

  getPartnersListFromAlgolia(
    query: string,
    searchParams?: Pick<
      SearchParamsObject,
      'page' | 'hitsPerPage' | 'facetFilters' | 'aroundLatLng' | 'aroundRadius' | 'filters' | 'facets'
    >
  ): Observable<SearchResponse<PlatformSearchResponse>> {
    return this.#getClient().pipe(
      switchMap((client) =>
        from(
          client.searchSingleIndex<PlatformSearchResponse>(
            {
              indexName: environment.algolia.index.partners,
              searchParams: {
                query,
                ...(searchParams ?? {}),
              },
            },
            {
              cacheable: false,
            }
          )
        )
      )
    );
  }

  readonly getPartnersList = (
    query: string,
    searchParams?: Pick<
      SearchParamsObject,
      'page' | 'hitsPerPage' | 'facetFilters' | 'aroundLatLng' | 'aroundRadius' | 'filters' | 'facets'
    >
  ): Observable<{ partners: PartnerOverview[]; pagination: PaginationOption }> => {
    return this.getPartnersListFromAlgolia(query, searchParams).pipe(
      map(({ hits, page, nbHits, nbPages, hitsPerPage }) => ({
        partnersIds: hits.map(({ objectID }, index) => `partnerIds[${index}]=${objectID}`),
        pagination: {
          currentPage: page,
          itemsPerPage: hitsPerPage,
          itemsCount: nbHits,
          pagesCount: nbPages,
        },
      })),
      switchMap((partners) => {
        return combineLatest([
          partners.partnersIds.length
            ? this.#http.get<PartnerOverview[]>(
                `${environment.api.obendy}/v1/admin/partner-revisions?${partners.partnersIds.join('&')}`
              )
            : of([]),
          of(partners.pagination),
        ]);
      }),
      map(([partners, pagination]) => ({
        partners,
        pagination,
      }))
    );
  };

  readonly getPartnerRevision = ({ partnerId }: Params): Observable<PartnerRevision> =>
    this.#http
      .get<PartnerRevision>(`${environment.api.obendy}/v2/admin/partner-revision/${partnerId}/latest`)
      .pipe(catchError(this.#handleError));

  readonly patch = (
    partnerId: string,
    partnerRevisionPatch: PartialDeep<PartnerRevision>
  ): Observable<PartnerRevision> =>
    this.#http
      .patch<PartnerRevision>(`${environment.api.obendy}/v4/admin/partner-revision/${partnerId}`, partnerRevisionPatch)
      .pipe(catchError(this.#handleError));

  readonly publish = (publicationUrl: string): Observable<void> => this.#http.put<undefined>(publicationUrl, {});

  readonly validate = (validationUrl: string): Observable<Violation[]> => this.#http.get<Violation[]>(validationUrl);

  readonly archive = (partnerId: string): Observable<PartnerRevision> =>
    this.#http
      .patch<PartnerRevision>(`${environment.api.obendy}/v4/admin/partner/${partnerId}/archive`, null)
      .pipe(catchError(this.#handleError));

  readonly unarchive = (partnerId: string): Observable<PartnerRevision> =>
    this.#http
      .patch<PartnerRevision>(`${environment.api.obendy}/v4/admin/partner/${partnerId}/unarchive`, null)
      .pipe(catchError(this.#handleError));

  readonly create = (partner: CreatePartnerRequest): Observable<Pick<PartnerRevision, 'id'>> =>
    this.#http
      .post<Pick<PartnerRevision, 'id'>>(`${environment.api.obendy}/v3/admin/partner`, partner)
      .pipe(catchError(this.#handleError));

  readonly replicate = ({
    id,
    name,
    productName,
    locale,
  }: ReplicatePartnerRequest): Observable<Pick<PartnerRevision, 'id'>> =>
    this.#http
      .post<Pick<PartnerRevision, 'id'>>(`${environment.api.obendy}/v3/admin/partner/${id}/duplicate`, {
        name,
        productName,
        locale,
      })
      .pipe(catchError(this.#handleError));

  readonly delete = (partnerId: string): Observable<PartnerRevision> =>
    this.#http
      .delete<PartnerRevision>(`${environment.api.obendy}/v4/admin/partner/${partnerId}`)
      .pipe(catchError(this.#handleError));

  readonly lock = (partnerId: string): Observable<void> =>
    this.#http.patch<undefined>(`${environment.api.obendy}/v3/admin/partner-revision/${partnerId}/lock`, null);

  readonly unlock = (partnerId: string): Observable<void> =>
    this.#http.patch<undefined>(`${environment.api.obendy}/v3/admin/partner-revision/${partnerId}/unlock`, null);

  readonly beaconUnlock = (partnerId: string, userId: string): void => {
    void fetch(`${environment.api.obendy}/v3/admin/partner-revision/${partnerId}/unlock`, {
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'POST',
      keepalive: true,
      body: JSON.stringify({ userId: userId }),
    });
  };

  readonly getRevisions = ({
    partnerId,
    sort,
    order,
    offset,
    size,
  }: {
    partnerId: string;
    sort: Active;
    order: Direction;
    offset: number;
    size: number;
  }): Observable<PartnerHistoryInterface> =>
    this.#http.get<PartnerHistoryInterface>(`${environment.api.obendy}/v3/partner/${partnerId}/history`, {
      params: { offset, size, sort, order },
    });

  readonly #handleError = (error: HttpErrorResponse): Observable<never> => throwError(() => error);

  /*
  This method checks if a resource is deletable.
  Will throw an error if the resource is not deletable.
  Will return 204 status if the resource is deletable.
   */
  readonly checkResourceIsDeletable = (
    partnerId: string,
    resourceType: PartnerRevisionDependencyEnum,
    resourceId: string
  ): Observable<unknown> =>
    this.#http.get<unknown>(
      `${environment.api.obendy}/v3/partner-revision/${partnerId}/check-is-deletable/${resourceType}/${resourceId}`
    );

  readonly publishPlatform = (partnerId: string): Observable<void> =>
    this.#http.put<undefined>(`${environment.api.obendy}/v4/partner-revision/${partnerId}/publish`, {});

  readonly validatePublication = (partnerId: string): Observable<Violation[]> =>
    this.#http.get<Violation[]>(`${environment.api.obendy}/v4/partner-revision/${partnerId}/validate`);
}
